import { useState, useMemo, useEffect, type DragEvent, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import axios from 'axios';
import dayjs, { type Dayjs } from 'dayjs';
import toast from 'react-hot-toast';
import {
  Sparkles, Plus, TrendingUp, X, Save, Send, Wand2, ChevronLeft, ChevronRight,
} from 'lucide-react';
import clsx from 'clsx';
import { UpgradeWallModal } from '../components/UpgradeWallModal';

interface UsageInfo {
  usage: { posts: { used: number; limit: number } };
}

type PostStatus = 'draft' | 'ready' | 'published' | 'failed';

interface Post {
  id: string;
  text: string;
  media_url?: string | null;
  scheduled_at: string; // ISO
  status: PostStatus;
  channel_id: string;
}

interface Channel {
  id: string;
  title: string;
}

interface Trend {
  id: string;
  title: string;
  score: number;
  niche?: string;
}

interface PlanSuggestion {
  date: string;
  text: string;
  topic?: string;
}

const STATUS_CLASS: Record<PostStatus, string> = {
  draft: 'bg-slate-200 text-slate-700 border-slate-300',
  ready: 'bg-blue-100 text-blue-800 border-blue-300',
  published: 'bg-green-100 text-green-800 border-green-300',
  failed: 'bg-red-100 text-red-800 border-red-300',
};

const MAX_LEN = 4096;

/** Returns true when the error is a 404/502/503 from a backend service that is
 *  not yet deployed. In these cases callers should treat the response as empty
 *  data rather than surface an error to the user. */
const isServiceUnavailable = (e: unknown): boolean =>
  axios.isAxiosError(e) && [404, 502, 503].includes(e.response?.status ?? 0);

function monthGrid(month: Dayjs): Dayjs[] {
  // 7×5 = 35 cells starting from first Monday of the grid covering the month start.
  const startOfMonth = month.startOf('month');
  // dayjs default: Sunday=0; we want Monday-first
  const dow = (startOfMonth.day() + 6) % 7; // 0..6 with Monday=0
  const gridStart = startOfMonth.subtract(dow, 'day');
  return Array.from({ length: 35 }, (_, i) => gridStart.add(i, 'day'));
}

export function ContentFactory(): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [month, setMonth] = useState<Dayjs>(dayjs().startOf('month'));
  const [channelId, setChannelId] = useState<string>('');
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [showAIPlan, setShowAIPlan] = useState(false);
  const [showWall, setShowWall] = useState(false);

  // Freemium-wall (§2 GROWTH-OS-v9): та же queryKey, что в Billing.tsx — общий кэш.
  // Fail-open при недоступности billing-сервиса (лимит=-1 = не блокируем создание
  // постов из-за транзиентного сбоя стороннего сервиса — это не security-контроль).
  const usageQ = useQuery<UsageInfo>({
    queryKey: ['billing', 'subscription'],
    queryFn: async () => {
      try {
        return (await api.get<UsageInfo>('/payments/subscription/current')).data;
      } catch {
        return { usage: { posts: { used: 0, limit: -1 } } };
      }
    },
    staleTime: 30_000,
  });

  const postsLimitReached = (): boolean => {
    const { used, limit } = usageQ.data?.usage.posts ?? { used: 0, limit: -1 };
    if (limit < 0) return false; // безлимит (Pro+) или лимит ещё не загружен
    if (used < limit) return false;
    setShowWall(true);
    return true;
  };

  // Channels — 404/502/503 → treat as empty (service not deployed yet)
  const channelsQ = useQuery<Channel[]>({
    queryKey: ['channels'],
    queryFn: async () => {
      try {
        const r = await api.get<Channel[]>('/channels');
        return r.data ?? [];
      } catch (e) {
        if (isServiceUnavailable(e)) return [];
        throw e;
      }
    },
  });

  useEffect(() => {
    if (!channelId && channelsQ.data && channelsQ.data.length > 0) {
      setChannelId(channelsQ.data[0]!.id);
    }
  }, [channelsQ.data, channelId]);

  const from = month.startOf('month').toISOString();
  const to = month.endOf('month').toISOString();

  // Posts — 404/502/503 → treat as empty array, no toast
  const postsQ = useQuery<Post[]>({
    queryKey: ['scheduler', channelId, from, to],
    queryFn: async () => {
      try {
        const r = await api.get<Post[]>('/scheduler/schedule', {
          params: { channel_id: channelId, from, to },
        });
        return r.data ?? [];
      } catch (e) {
        if (isServiceUnavailable(e)) return [];
        throw e;
      }
    },
    enabled: Boolean(channelId),
  });

  // Trends — 404/502/503 → treat as empty array, no toast
  const trendsQ = useQuery<Trend[]>({
    queryKey: ['trends'],
    queryFn: async () => {
      try {
        const r = await api.get<Trend[]>('/trends');
        return (r.data ?? []).slice(0, 10);
      } catch (e) {
        if (isServiceUnavailable(e)) return [];
        throw e;
      }
    },
  });

  // Mutations — toast.error only on user-triggered create/update/delete failures
  const createPostMut = useMutation({
    mutationFn: async (payload: Partial<Post>) => {
      const r = await api.post<Post>('/scheduler/schedule', payload);
      return r.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['scheduler'] });
      toast.success(t('content.toast.created'));
    },
    onError: () => toast.error(t('common.error')),
  });

  const updatePostMut = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<Post> }) => {
      const r = await api.patch<Post>(`/scheduler/${id}`, patch);
      return r.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['scheduler'] });
      toast.success(t('content.toast.updated'));
    },
    onError: () => toast.error(t('common.error')),
  });

  const deletePostMut = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/scheduler/schedule/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['scheduler'] });
      setSelectedPostId(null);
      toast.success(t('content.toast.deleted'));
    },
    onError: () => toast.error(t('common.error')),
  });

  const postsByDay = useMemo(() => {
    const map = new Map<string, Post[]>();
    (postsQ.data ?? []).forEach((p) => {
      const key = dayjs(p.scheduled_at).format('YYYY-MM-DD');
      const arr = map.get(key) ?? [];
      arr.push(p);
      map.set(key, arr);
    });
    return map;
  }, [postsQ.data]);

  const selectedPost = (postsQ.data ?? []).find((p) => p.id === selectedPostId) ?? null;

  // Drag-and-drop
  const handleDragStart = (e: DragEvent<HTMLDivElement>, postId: string): void => {
    e.dataTransfer.setData('text/plain', postId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDayDrop = (e: DragEvent<HTMLDivElement>, day: Dayjs): void => {
    e.preventDefault();
    const postId = e.dataTransfer.getData('text/plain');
    if (!postId) return;
    const post = (postsQ.data ?? []).find((p) => p.id === postId);
    if (!post) return;
    const original = dayjs(post.scheduled_at);
    const next = day.hour(original.hour()).minute(original.minute()).toISOString();
    updatePostMut.mutate({ id: postId, patch: { scheduled_at: next } });
  };

  const handleAddPost = (date?: Dayjs): void => {
    if (!channelId) {
      toast.error(t('content.noChannel'));
      return;
    }
    if (postsLimitReached()) return;
    const when = (date ?? dayjs()).hour(12).minute(0).second(0).toISOString();
    createPostMut.mutate({
      text: '',
      scheduled_at: when,
      channel_id: channelId,
      status: 'draft',
    });
  };

  const handleTrendClick = (trend: Trend): void => {
    if (!channelId) {
      toast.error(t('content.noChannel'));
      return;
    }
    if (postsLimitReached()) return;
    createPostMut.mutate({
      text: trend.title,
      scheduled_at: dayjs().add(1, 'day').hour(12).minute(0).toISOString(),
      channel_id: channelId,
      status: 'draft',
    });
  };

  const weekdays = useMemo(
    () => ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
    [],
  );
  const cells = useMemo(() => monthGrid(month), [month]);

  // Determine whether channels service is unavailable (empty but no real error)
  const noChannels = !channelsQ.isLoading && (channelsQ.data ?? []).length === 0;

  return (
    <div className="flex h-full">
      {/* Left: Trends */}
      <aside className="w-64 border-r border-slate-200 bg-white p-4 overflow-y-auto" aria-label="trends-panel">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp size={18} className="text-brand-primary" />
          <h2 className="font-semibold text-brand-ink">{t('content.trends')}</h2>
        </div>
        {trendsQ.isLoading && <div className="text-sm text-brand-muted">{t('common.loading')}</div>}
        {/* Only show an error state for unexpected errors, not service-unavailable */}
        {trendsQ.isError && <div className="text-sm text-brand-muted">{t('content.empty.trends')}</div>}
        {!trendsQ.isLoading && !trendsQ.isError && (trendsQ.data ?? []).length === 0 && (
          <div className="text-sm text-brand-muted">{t('content.empty.trends')}</div>
        )}
        <ul className="space-y-2">
          {(trendsQ.data ?? []).map((tr) => (
            <li key={tr.id}>
              <button
                type="button"
                onClick={() => handleTrendClick(tr)}
                className="w-full text-left p-2 rounded-lg border border-slate-200 hover:border-brand-primary hover:bg-brand-primary/5 transition text-sm"
              >
                <div className="font-medium text-brand-ink line-clamp-2">{tr.title}</div>
                <div className="text-xs text-brand-muted mt-1">score: {tr.score.toFixed(1)}</div>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      {/* Center: Calendar */}
      <section className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="flex items-center gap-3 p-4 border-b border-slate-200 bg-white flex-wrap">
          <select
            aria-label="channel-select"
            className="input"
            value={channelId}
            onChange={(e) => setChannelId(e.target.value)}
          >
            <option value="">{t('content.selectChannel')}</option>
            {(channelsQ.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>

          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="prev-month"
              onClick={() => setMonth(month.subtract(1, 'month'))}
              className="p-2 rounded hover:bg-slate-100"
            >
              <ChevronLeft size={18} />
            </button>
            <div className="font-semibold w-40 text-center">{month.format('MMMM YYYY')}</div>
            <button
              type="button"
              aria-label="next-month"
              onClick={() => setMonth(month.add(1, 'month'))}
              className="p-2 rounded hover:bg-slate-100"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => { if (!postsLimitReached()) setShowAIPlan(true); }}
              className="btn-secondary inline-flex items-center gap-2"
            >
              <Sparkles size={16} /> {t('content.aiPlan')}
            </button>
            <button
              type="button"
              onClick={() => handleAddPost()}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Plus size={16} /> {t('content.newPost')}
            </button>
          </div>
        </header>

        {/* Calendar grid */}
        <div className="flex-1 overflow-auto p-4">
          {postsQ.isLoading && <div className="text-brand-muted">{t('common.loading')}</div>}
          {/* postsQ.isError here means a real unexpected error (not 404/502/503) */}
          {postsQ.isError && <div className="text-red-600">{t('common.error')}</div>}

          {/* No channels connected yet — empty state instead of error */}
          {noChannels && !channelsQ.isLoading && (
            <div className="flex flex-col items-center justify-center h-48 text-brand-muted gap-2">
              <TrendingUp size={32} className="opacity-30" />
              <p className="text-sm">{t('content.empty.noChannels', 'No channels connected yet')}</p>
            </div>
          )}

          {!noChannels && (
            <div className="grid grid-cols-7 gap-0 border-l border-t border-slate-200" role="grid" aria-label="calendar">
              {weekdays.map((d) => (
                <div key={d} className="px-2 py-1 text-xs font-semibold text-brand-muted bg-slate-50 border-r border-b border-slate-200">
                  {d}
                </div>
              ))}
              {cells.map((day) => {
                const key = day.format('YYYY-MM-DD');
                const inMonth = day.month() === month.month();
                const dayPosts = postsByDay.get(key) ?? [];
                return (
                  <div
                    key={key}
                    role="gridcell"
                    data-testid={`day-${key}`}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => handleDayDrop(e, day)}
                    className={clsx(
                      'min-h-24 p-1 border-r border-b border-slate-200 flex flex-col gap-1',
                      inMonth ? 'bg-white' : 'bg-slate-50/60',
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span className={clsx('text-xs', inMonth ? 'text-brand-ink' : 'text-brand-muted')}>
                        {day.date()}
                      </span>
                      <button
                        type="button"
                        aria-label={`add-post-${key}`}
                        className="text-brand-muted hover:text-brand-primary"
                        onClick={() => handleAddPost(day)}
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                    {dayPosts.map((p) => (
                      <div
                        key={p.id}
                        role="button"
                        tabIndex={0}
                        draggable
                        onDragStart={(e) => handleDragStart(e, p.id)}
                        onClick={() => setSelectedPostId(p.id)}
                        data-testid={`post-${p.id}`}
                        className={clsx(
                          'text-[11px] px-1.5 py-1 rounded border cursor-pointer truncate',
                          STATUS_CLASS[p.status],
                        )}
                        title={p.text}
                      >
                        {dayjs(p.scheduled_at).format('HH:mm')} · {p.text.slice(0, 30) || t('content.untitled')}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Right: Post editor */}
      {selectedPost && (
        <PostEditor
          post={selectedPost}
          channels={channelsQ.data ?? []}
          onClose={() => setSelectedPostId(null)}
          onSave={(patch) => updatePostMut.mutate({ id: selectedPost.id, patch })}
          onSchedule={(patch) => updatePostMut.mutate({ id: selectedPost.id, patch: { ...patch, status: 'ready' } })}
          onDelete={() => deletePostMut.mutate(selectedPost.id)}
        />
      )}

      {showAIPlan && (
        <AIPlanModal
          channelId={channelId}
          onClose={() => setShowAIPlan(false)}
          onApplied={() => {
            setShowAIPlan(false);
            void qc.invalidateQueries({ queryKey: ['scheduler'] });
          }}
        />
      )}

      <UpgradeWallModal
        open={showWall}
        onClose={() => setShowWall(false)}
        reason="posts"
        used={usageQ.data?.usage.posts.used ?? 0}
        limit={usageQ.data?.usage.posts.limit ?? 0}
      />
    </div>
  );
}

/* ------------------ Post Editor ------------------ */

interface PostEditorProps {
  post: Post;
  channels: Channel[];
  onClose: () => void;
  onSave: (patch: Partial<Post>) => void;
  onSchedule: (patch: Partial<Post>) => void;
  onDelete: () => void;
}

function PostEditor({ post, channels, onClose, onSave, onSchedule, onDelete }: PostEditorProps): JSX.Element {
  const { t } = useTranslation();
  const [text, setText] = useState(post.text);
  const [mediaUrl, setMediaUrl] = useState(post.media_url ?? '');
  const [scheduledAt, setScheduledAt] = useState<string>(
    dayjs(post.scheduled_at).format('YYYY-MM-DDTHH:mm'),
  );
  const [channelId, setChannelId] = useState(post.channel_id);
  const [improving, setImproving] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  useEffect(() => {
    setText(post.text);
    setMediaUrl(post.media_url ?? '');
    setScheduledAt(dayjs(post.scheduled_at).format('YYYY-MM-DDTHH:mm'));
    setChannelId(post.channel_id);
    setAiError(null);
  }, [post]);

  const buildPatch = (): Partial<Post> => ({
    text,
    media_url: mediaUrl || null,
    scheduled_at: dayjs(scheduledAt).toISOString(),
    channel_id: channelId,
  });

  const aiImprove = async (mode: 'rewrite' | 'shorter' | 'cta'): Promise<void> => {
    setImproving(true);
    setAiError(null);
    try {
      const r = await api.post<{ text: string }>('/ai/improve', { text, mode });
      setText(r.data.text);
      toast.success(t('content.toast.aiImproved'));
    } catch (e) {
      // AI service unavailable → show inline message, not a persistent toast
      if (isServiceUnavailable(e)) {
        setAiError(t('content.editor.aiUnavailable', 'AI unavailable'));
      } else {
        // Unexpected error — still inline to avoid toast spam in the editor
        setAiError(t('common.error'));
      }
    } finally {
      setImproving(false);
    }
  };

  const remaining = MAX_LEN - text.length;

  return (
    <aside className="w-96 border-l border-slate-200 bg-white p-4 overflow-y-auto" aria-label="post-editor">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-brand-ink">{t('content.editor.title')}</h2>
        <button type="button" aria-label="close-editor" onClick={onClose} className="text-brand-muted hover:text-brand-ink">
          <X size={18} />
        </button>
      </div>

      <label className="block text-sm font-medium mb-1">{t('content.editor.text')}</label>
      <textarea
        aria-label="post-text"
        className="input w-full h-40 font-mono text-sm"
        maxLength={MAX_LEN}
        value={text}
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setText(e.target.value)}
      />
      <div className={clsx('text-xs mt-1', remaining < 100 ? 'text-red-600' : 'text-brand-muted')}>
        {text.length} / {MAX_LEN}
      </div>

      <label className="block text-sm font-medium mt-3 mb-1">{t('content.editor.media')}</label>
      <input
        aria-label="post-media"
        type="url"
        placeholder="https://…"
        className="input w-full"
        value={mediaUrl}
        onChange={(e) => setMediaUrl(e.target.value)}
      />

      <label className="block text-sm font-medium mt-3 mb-1">{t('content.editor.time')}</label>
      <input
        aria-label="post-time"
        type="datetime-local"
        className="input w-full"
        value={scheduledAt}
        onChange={(e) => setScheduledAt(e.target.value)}
      />

      <label className="block text-sm font-medium mt-3 mb-1">{t('content.editor.channel')}</label>
      <select
        aria-label="post-channel"
        className="input w-full"
        value={channelId}
        onChange={(e) => setChannelId(e.target.value)}
      >
        {channels.map((c) => (
          <option key={c.id} value={c.id}>{c.title}</option>
        ))}
      </select>

      <div className="mt-4">
        <div className="text-xs font-semibold text-brand-muted mb-2 uppercase tracking-wide">
          {t('content.editor.aiTitle')}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={improving} onClick={() => void aiImprove('rewrite')} className="btn-secondary text-xs inline-flex items-center gap-1">
            <Wand2 size={12} /> {t('content.editor.aiRewrite')}
          </button>
          <button type="button" disabled={improving} onClick={() => void aiImprove('shorter')} className="btn-secondary text-xs">
            {t('content.editor.aiShorter')}
          </button>
          <button type="button" disabled={improving} onClick={() => void aiImprove('cta')} className="btn-secondary text-xs">
            {t('content.editor.aiCta')}
          </button>
        </div>
        {/* Inline AI error — does not use toast */}
        {aiError && (
          <p className="text-xs text-amber-600 mt-2" role="alert">{aiError}</p>
        )}
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        <button type="button" onClick={() => onSave(buildPatch())} className="btn-secondary inline-flex items-center gap-1">
          <Save size={14} /> {t('common.save')}
        </button>
        <button type="button" onClick={() => onSchedule(buildPatch())} className="btn-primary inline-flex items-center gap-1">
          <Send size={14} /> {t('content.editor.schedule')}
        </button>
        <button type="button" onClick={onDelete} className="text-red-600 hover:text-red-700 text-sm ml-auto">
          {t('common.delete')}
        </button>
      </div>
    </aside>
  );
}

/* ------------------ AI Plan Modal ------------------ */

interface AIPlanModalProps {
  channelId: string;
  onClose: () => void;
  onApplied: () => void;
}

function AIPlanModal({ channelId, onClose, onApplied }: AIPlanModalProps): JSX.Element {
  const { t } = useTranslation();
  const [topic, setTopic] = useState('');
  const [audience, setAudience] = useState('');
  const [days, setDays] = useState(30);
  const [perDay, setPerDay] = useState(1);
  const [language, setLanguage] = useState('ru');
  const [suggestions, setSuggestions] = useState<PlanSuggestion[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const generate = async (): Promise<void> => {
    setLoading(true);
    setAiError(null);
    try {
      const r = await api.post<{ suggestions: PlanSuggestion[] }>('/ai/plan', {
        topic, audience, days, posts_per_day: perDay, language,
      });
      setSuggestions(r.data.suggestions ?? []);
    } catch (e) {
      // AI service unavailable → show inline error inside the modal, not a toast
      if (isServiceUnavailable(e)) {
        setAiError(t('content.editor.aiUnavailable', 'AI unavailable'));
      } else {
        setAiError(t('common.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const applyAll = async (): Promise<void> => {
    if (!suggestions || !channelId) {
      toast.error(t('content.noChannel'));
      return;
    }
    setApplying(true);
    try {
      await Promise.all(suggestions.map((s) =>
        api.post('/scheduler/schedule', {
          text: s.text,
          scheduled_at: s.date,
          channel_id: channelId,
          status: 'draft',
        }),
      ));
      toast.success(t('content.toast.planApplied'));
      onApplied();
    } catch {
      // User-triggered action (applying a plan) → toast is appropriate
      toast.error(t('common.error'));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" role="dialog" aria-label="ai-plan-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="font-semibold inline-flex items-center gap-2">
            <Sparkles size={18} className="text-brand-primary" /> {t('content.aiPlan')}
          </h2>
          <button type="button" aria-label="close-modal" onClick={onClose} className="text-brand-muted hover:text-brand-ink">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 grid grid-cols-2 gap-3 overflow-y-auto">
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">{t('content.plan.topic')}</label>
            <input className="input w-full" aria-label="plan-topic" value={topic} onChange={(e) => setTopic(e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">{t('content.plan.audience')}</label>
            <input className="input w-full" aria-label="plan-audience" value={audience} onChange={(e) => setAudience(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('content.plan.days')}</label>
            <input className="input w-full" type="number" min={1} max={90} aria-label="plan-days" value={days} onChange={(e) => setDays(Number(e.target.value))} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('content.plan.perDay')}</label>
            <input className="input w-full" type="number" min={1} max={10} aria-label="plan-perday" value={perDay} onChange={(e) => setPerDay(Number(e.target.value))} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('content.plan.language')}</label>
            <select className="input w-full" aria-label="plan-language" value={language} onChange={(e) => setLanguage(e.target.value)}>
              <option value="ru">RU</option>
              <option value="en">EN</option>
            </select>
          </div>

          {/* Inline AI error inside the modal */}
          {aiError && (
            <div className="col-span-2">
              <p className="text-sm text-amber-600" role="alert">{aiError}</p>
            </div>
          )}

          {suggestions && (
            <div className="col-span-2 mt-2">
              <div className="text-sm font-semibold mb-2">{t('content.plan.preview')} ({suggestions.length})</div>
              <ul className="space-y-1 max-h-60 overflow-y-auto border border-slate-200 rounded-lg p-2 bg-slate-50">
                {suggestions.map((s, i) => (
                  <li key={i} className="text-xs p-1.5 bg-white rounded border border-slate-200">
                    <span className="text-brand-muted">{dayjs(s.date).format('DD MMM HH:mm')}:</span>{' '}
                    <span className="text-brand-ink">{s.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary">{t('common.cancel')}</button>
          {!suggestions && (
            <button type="button" onClick={() => void generate()} disabled={loading || !topic} className="btn-primary inline-flex items-center gap-2">
              <Sparkles size={14} /> {loading ? t('common.loading') : t('content.plan.generate')}
            </button>
          )}
          {suggestions && (
            <button type="button" onClick={() => void applyAll()} disabled={applying} className="btn-primary">
              {applying ? t('common.loading') : t('content.plan.applyAll')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
